import React from "react";
import { Button, Col, ConfigProvider, Divider, Pagination, Row, Space, Table, Tag, Typography } from "antd";
import type { PaginationProps, TableProps } from "antd";
import "./CompanyTable.css";
import { BsPencilSquare, BsTrashFill } from "react-icons/bs";
import { ResponseCompanyMapping } from "src/entity/company/company-mapping.entity";
import { Switch } from "antd";
import { COMPANY_GROUP } from "src/entity/common/common.constant";

const { Text } = Typography;
interface CompanyTableProps {
  loadingSearch: boolean;
  data: ResponseCompanyMapping[];
  onClickEdit: (record: ResponseCompanyMapping) => void;
  deleteMappingCompany: (id: string) => void;
  loadingManageCompanyMap: boolean;
  page: number;
  setPage: (page: number) => void;
  canEditTable: boolean;
}

const CompanyTable: React.FC<CompanyTableProps> = ({
  loadingSearch,
  data,
  onClickEdit,
  loadingManageCompanyMap,
  deleteMappingCompany,
  page,
  setPage,
  canEditTable,
}) => {
  const columns: TableProps<ResponseCompanyMapping>["columns"] = [
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      render: (text) => <Text>{text}</Text>,
      align: "left",
    },
    {
      title: "Organization Type",
      dataIndex: "organization",
      key: "organization",
      render: (text) => <Text>{text}</Text>,
      align: "left",
    },
    {
      title: "Company Group",
      key: "companyGroupId",
      render: (_, record) => <Text>{record.companyGroupId.companyGroupName}</Text>,
      align: "left",
    },
    {
      title: "Company",
      key: "company",
      render: (_, reord) => <Text>{reord.company.companyName}</Text>,
      align: "left",
    },
    {
      title: "Company ID",
      key: "companyID",
      render: (_, record) => {
        return (
          <Col>
            {record.areaCompany.map((item, index) => (
              <div key={"cell-area-" + index}>
                <Text>{item.companyId}</Text>
                {index !== record.areaCompany.length - 1 && <Divider style={{ marginTop: 8, marginBottom: 4 }} />}
              </div>
            ))}
          </Col>
        );
      },
      align: "left",
    },
    {
      title: "Area",
      key: "area",
      render: (_, record) => {
        return (
          <Col>
            {record.areaCompany.map((item, i) => (
              <div key={"area-wrap" + i}>
                {item.area.map((area, index) => (
                  <Text key={"area-text" + index}>{area.areaName + (index === item.area.length - 1 ? "" : ", ")}</Text>
                ))}
                {i !== record.areaCompany.length - 1 && <Divider style={{ marginTop: 8, marginBottom: 4 }} />}
              </div>
            ))}
          </Col>
        );
      },
      align: "left",
    },
    {
      title: COMPANY_GROUP.SCGC_SUBSIDIARY,
      key: "scgSubsidiary",
      render: (_, record) => {
        const reportItem = record.reportVisible?.find((item) => item.companyGroup === COMPANY_GROUP.SCGC_SUBSIDIARY);
        return (
          <Space size="middle">
            <Switch disabled={true} checked={reportItem?.isActive || false} checkedChildren="Active" unCheckedChildren="Inactive" />
          </Space>
        );
      },
    },
    {
      title: COMPANY_GROUP.SCGC_JV,
      key: "scgJV",
      render: (_, record) => {
        const reportItem = record.reportVisible?.find((item) => item.companyGroup === COMPANY_GROUP.SCGC_JV);
        return (
          <Space size="middle">
            <Switch disabled={true} checked={reportItem?.isActive || false} checkedChildren="Active" unCheckedChildren="Inactive" />
          </Space>
        );
      },
    },
    {
      title: COMPANY_GROUP.SCGC_ALL,
      key: "scgAll",
      render: (_, record) => {
        const reportItem = record.reportVisible?.find((item) => item.companyGroup === COMPANY_GROUP.SCGC_ALL);
        return (
          <Space size="middle">
            <Switch disabled={true} checked={reportItem?.isActive || false} checkedChildren="Active" unCheckedChildren="Inactive" />
          </Space>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle" direction={"horizontal"}>
          <Button className="border-transparent bg-transparent" size={"small"} onClick={() => onClickEdit(record)} disabled={!canEditTable}>
            <BsPencilSquare size={18} />
          </Button>
          <Button
            className="border-transparent bg-transparent"
            size={"small"}
            onClick={() => deleteMappingCompany(record._id)}
            disabled={loadingManageCompanyMap || !canEditTable}
          >
            <BsTrashFill size={18} />
          </Button>
        </Space>
      ),
      align: "left",
    },
  ];

  return (
    <Row className="w-full" id={"wrap-company-table"}>
      <Table
        columns={columns}
        dataSource={data}
        className="w-full"
        loading={loadingSearch}
        pagination={{ showSizeChanger: false, pageSize: 10, current: page, onChange: (pa) => setPage(pa) }}
        scroll={{ x: "max-content" }}
        rowKey={(item) => `${item._id}`}
      />
    </Row>
  );
};

export default CompanyTable;
